// src/components/masters/DocumentType/DocumentTypeComponent.js
import React, { useLayoutEffect, useState, useEffect, useRef } from "react";
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { TextBox, CheckBox, SelectBox, NumberBox } from "devextreme-react";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { documentTypeService } from "../../../apis/apiServices/DocumentType";
import { documentCategoryService } from "../../../apis/apiServices/DocumentCategory";

export default function DocumentTypeComponent() {
  useLayoutEffect(() => {
    const el = document.querySelector("#pageTitle");
    if (el) el.innerHTML = "Document Type";
  }, []);

  const user = useSelector((state) => state.user?.data);
  const refDataGrid = useRef(null);
  const [list, setList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [model, setModel] = useState({
    id: 0,
    documentName: "",
    isIssueDateEnable: false,
    isExpiryDateEnable: false,
    isReminderAlertEnable: false,
    reminderAlertDefaultDays: null,
    isRenewable: false,
    haveDocumentNo: false,
    documentCategoryId: null,
    companyId: 0,
  });

  const normalizeToArray = (resp) => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    const d = resp.data ?? resp;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d.data)) return d.data;
    if (Array.isArray(d.items)) return d.items;
    if (Array.isArray(resp.items)) return resp.items;
    return [];
  };

  const mapServerToModel = (s) => {
    if (!s) return null;
    return {
      id: s.id ?? s.Id ?? 0,
      documentName: s.document_name ?? s.documentName ?? s.DocumentName ?? "",
      isIssueDateEnable: !!(
        s.is_issue_date_enable ??
        s.isIssueDateEnable ??
        s.IsIssueDateEnable
      ),
      isExpiryDateEnable: !!(
        s.is_expiry_date_enable ??
        s.isExpiryDateEnable ??
        s.IsExpiryDateEnable
      ),
      isReminderAlertEnable: !!(
        s.is_reminder_alert_enable ??
        s.isReminderAlertEnable ??
        s.IsReminderAlertEnable
      ),
      reminderAlertDefaultDays:
        s.reminer_alert_default_days ??
        s.ReminderAlertDefaultDays ??
        s.reminderAlertDefaultDays ??
        s.reminder_alert_default_days ??
        0,
      isRenewable: !!(s.is_renewable ?? s.isRenewable ?? s.IsRenewable),
      haveDocumentNo: !!(
        s.have_document_no ??
        s.haveDocumentNo ??
        s.HaveDocumentNo
      ),
      documentCategoryId:
        s.document_category_id ??
        s.documentCategoryId ??
        s.DocumentCategoryId ??
        0,
      documentCategoryName: s.documentCategoryName ?? "",
      companyId: s.company_id ?? s.companyId ?? s.CompanyId ?? 0,
      raw: s,
    };
  };

  const getAllCategories = async () => {
    try {
      const resp = await documentCategoryService.getDocumentCategoryList();
      const cats = normalizeToArray(resp);
      const mapped = cats.map((c) => ({
        id: c.id ?? c.ID ?? c.categoryId ?? c.category_id ?? c?.Id ?? null,
        category_name:
          c.category_name ?? c.categoryName ?? c.name ?? c.category ?? "",
        raw: c,
      }));
      setCategoryList(mapped);
    } catch (err) {
      console.error("[DocumentType] getAllCategories error", err);
      setCategoryList([]);
    }
  };

  const getList = async () => {
    try {
      if (refDataGrid?.current?.instance?.beginCustomLoading)
        refDataGrid.current.instance.beginCustomLoading();

      const resp = await documentTypeService.getDocumentTypeList();
      const types = normalizeToArray(resp);
      const normalized = types.map(mapServerToModel);
      setList(normalized);

      await getAllCategories();
    } catch (err) {
      console.error("[DocumentType] getList error", err);
      Swal.fire("Error", "Failed to fetch document types", "error");
    } finally {
      if (refDataGrid?.current?.instance?.endCustomLoading)
        refDataGrid.current.instance.endCustomLoading();
    }
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line
  }, []);

  const openAddModal = async () => {
    await getAllCategories();
    setModel({
      id: 0,
      documentName: "",
      isIssueDateEnable: false,
      isExpiryDateEnable: false,
      isReminderAlertEnable: false,
      reminderAlertDefaultDays: null,
      isRenewable: false,
      haveDocumentNo: false,
      documentCategoryId: null,
      companyId: 0,
    });
    setErrors({});
    setIsSubmitted(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsSubmitted(false);
    setErrors({});
    setModel({
      id: 0,
      documentName: "",
      isIssueDateEnable: false,
      isExpiryDateEnable: false,
      isReminderAlertEnable: false,
      reminderAlertDefaultDays: null,
      isRenewable: false,
      haveDocumentNo: false,
      documentCategoryId: null,
      companyId: 0,
    });
  };

  const handleRowClick = (e) => {
    const d = e?.data || {};
    const mapped = mapServerToModel(d);
    if (!mapped) return;
    const rad = mapped.reminderAlertDefaultDays;
    mapped.reminderAlertDefaultDays =
      rad === null ? null : Number.isFinite(Number(rad)) ? Number(rad) : null;
    setModel(mapped);
  };

  const handleInputChange = (evtOrValue, field) => {
    const value =
      evtOrValue && typeof evtOrValue === "object" && "value" in evtOrValue
        ? evtOrValue.value
        : evtOrValue;

    if (field === "isReminderAlertEnable") {
      const boolVal = !!value;
      setModel((prev) => ({
        ...prev,
        isReminderAlertEnable: boolVal,
        reminderAlertDefaultDays: boolVal
          ? prev.reminderAlertDefaultDays
          : null,
      }));
      setErrors((prev) => ({ ...prev, reminderAlertDefaultDays: "" }));
      return;
    }

    if (field === "reminderAlertDefaultDays") {
      let rad = null;
      if (value === null || typeof value === "undefined" || value === "") {
        rad = null;
      } else if (typeof value === "number") {
        rad = Number.isFinite(value) ? Math.trunc(value) : null;
      } else {
        const parsed = Number.parseInt(String(value).trim(), 10);
        rad = Number.isFinite(parsed) ? parsed : null;
      }
      setErrors((prev) => ({ ...prev, reminderAlertDefaultDays: "" }));
      setModel((prev) => ({ ...prev, reminderAlertDefaultDays: rad }));
      return;
    }

    setErrors((prev) => ({ ...prev, [field]: "" }));
    setModel((prev) => ({ ...prev, [field]: value }));
  };

  const renderSwalError = (backendDataOrMessage) => {
    if (!backendDataOrMessage) {
      Swal.fire("Error", "Something went wrong", "error");
      return;
    }

    if (typeof backendDataOrMessage === "string") {
      Swal.fire("Error", backendDataOrMessage, "error");
      return;
    }

    const pretty = JSON.stringify(backendDataOrMessage, null, 2);
    Swal.fire({
      title: "Error",
      html: `<pre style="text-align:left;white-space:pre-wrap">${pretty}</pre>`,
      icon: "error",
      width: 700,
    });
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setIsSubmitted(true);

    const newErrors = {};
    if (!model.documentCategoryId)
      newErrors.documentCategoryId = "Please select document category";
    if (!model.documentName)
      newErrors.documentName = "Please enter Document name";
    if (
      model.isReminderAlertEnable &&
      (model.reminderAlertDefaultDays === null ||
        model.reminderAlertDefaultDays === undefined ||
        model.reminderAlertDefaultDays === "")
    ) {
      newErrors.reminderAlertDefaultDays = "Please enter reminder days";
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setIsSubmitted(false);
      return;
    }

    try {
      const body = {
        id: model.id,
        documentName: model.documentName,
        isIssueDateEnable: model.isIssueDateEnable,
        isExpiryDateEnable: model.isExpiryDateEnable,
        isReminderAlertEnable: model.isReminderAlertEnable,
        reminderAlertDefaultDays: model.reminderAlertDefaultDays,
        isRenewable: model.isRenewable,
        haveDocumentNo: model.haveDocumentNo,
        documentCategoryId: model.documentCategoryId,
        companyId: model.companyId ?? 0,
        ModifiedBy: user?.userId ?? null,
      };

      let resp;
      if (body.id && body.id > 0)
        resp = await documentTypeService.updateDocumentType(body);
      else resp = await documentTypeService.addDocumentType(body);

      const ok =
        resp?.isSuccess ??
        resp?.success ??
        resp?.data?.isSuccess ??
        resp === true;
      const serverMessage = resp?.message ?? resp?.data?.message ?? "Saved";
      const successMessage =
        body.id && body.id > 0
          ? "Document Type Updated Successfully"
          : "Document Type Created Successfully";

      if (!ok) {
        renderSwalError(resp?.errors ?? serverMessage ?? resp);
        setErrors(resp?.errors || {});
        setIsSubmitted(false);
        return;
      }

      await getList();
      console.debug(
        "[DocumentTypeComponent] server response:",
        resp,
        "serverMessage:",
        serverMessage
      );
      Swal.fire("Success", successMessage, "success").then(() => closeModal());
    } catch (err) {
      console.error("[DocumentType] submit error", err);
      const backendData = err?.response?.data ?? err?.message;
      renderSwalError(backendData);
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the document type",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        if (refDataGrid?.current?.instance?.beginCustomLoading)
          refDataGrid.current.instance.beginCustomLoading();
        const resp = await documentTypeService.deleteDocumentType(id);

        const ok =
          resp === true ||
          resp?.isSuccess === true ||
          resp?.success === true ||
          resp?.status === 204 ||
          (resp &&
            typeof resp === "object" &&
            (resp?.message || resp?.isSuccess || resp?.success));

        if (!ok) {
          const message =
            resp?.message ?? resp?.data?.message ?? "Failed to delete";
          renderSwalError(message ?? resp);
          return;
        }

        // find name from current list if available
        const deletedName = list.find((item) => item.id === id)?.documentName;
        const successMsg = deletedName
          ? `Document Type "${deletedName}" Deleted Successfully`
          : "Document Type Deleted Successfully";

        Swal.fire("Success", successMsg, "success");
        await getList();
      } catch (err) {
        console.error("[DocumentType] delete error (component):", {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
        });
        const backendMessage =
          err?.response?.data?.message ??
          err?.response?.data ??
          err?.message ??
          "Something went wrong";
        renderSwalError(backendMessage);
      } finally {
        if (refDataGrid?.current?.instance?.endCustomLoading)
          refDataGrid.current.instance.endCustomLoading();
      }
    });
  };

  const renderActionCell = (cellData) => (
    <span>
      <Icon
        className="iconify-size mr-2"
        title="Edit"
        icon="solar:pen-line-duotone"
        style={{ cursor: "pointer" }}
        onClick={() => {
          handleRowClick(cellData);
          setIsModalOpen(true);
        }}
      />
      <Icon
        className="iconify-size"
        title="Delete"
        icon="fluent:delete-32-regular"
        style={{ cursor: "pointer", marginLeft: 8 }}
        onClick={() => handleDelete(cellData.data?.id)}
      />
    </span>
  );

  const renderStatusCell = ({ value }) => {
    const isActive = value === true || value === 1;
    return <div>{isActive ? "Enabled" : "Disabled"}</div>;
  };

  return (
    <>
      <div className="content">
        <div className="card">
          <div className="card-body">
            <DataGrid
              dataSource={list}
              showColumnLines={false}
              ref={refDataGrid}
              showRowLines
              showBorders
              onRowClick={(e) => handleRowClick(e)}
              allowColumnReordering
              columnAutoWidth
              width={"100%"}
              onToolbarPreparing={(e) => {
                e.toolbarOptions.items.unshift(
                  {
                    location: "before",
                    widget: "dxButton",
                    options: {
                      icon: "plus",
                      text: "Add",
                      type: "default",
                      stylingMode: "contained",
                      onClick: () => openAddModal(),
                    },
                  },
                  {
                    location: "before",
                    widget: "dxButton",
                    options: {
                      icon: "refresh",
                      text: "Refresh",
                      type: "default",
                      stylingMode: "outlined",
                      onClick: () => getList(),
                    },
                  }
                );
              }}
            >
              <HeaderFilter visible={false} />
              <Column width={60} caption={""} cellRender={renderActionCell} />
              <Column dataField="documentName" caption="Document Name" />
              <Column dataField="documentCategoryName" caption="Category" />
              <Column
                dataField="isIssueDateEnable"
                caption="Issue Date"
                cellRender={renderStatusCell}
              />
              <Column
                dataField="isExpiryDateEnable"
                caption="Expiry Date"
                cellRender={renderStatusCell}
              />
              <Column
                dataField="reminderAlertDefaultDays"
                caption="Reminder Days"
              />
              <SearchPanel visible placeholder="Search..." width={240} />
              <Paging defaultPageSize={15} />
              <Pager displayMode="compact" showInfo visible />
              <Export enabled={false} fileName="DocumentTypes" />
            </DataGrid>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        ariaHideApp={false}
        isOpen={isModalOpen}
        className="react-modal medium-modal"
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={false}
      >
        <form
          onSubmit={handleSubmit}
          style={isSubmitted ? { pointerEvents: "none", opacity: 0.4 } : {}}
        >
          <div className="modal-header">
            <div className="modal-title">
              {model.id === 0
                ? "Add Document Type"
                : `Update Document Type [${model.documentName}]`}
            </div>
          </div>

          <div className="modal-body">
            <div className="row">
              {/* Document Category first */}
              <div className="col-12">
                <div className="form-group">
                  <label>
                    Document Category <span className="text-danger">*</span>
                  </label>
                  <SelectBox
                    dataSource={categoryList}
                    displayExpr={(item) =>
                      item?.category_name ??
                      item?.raw?.category_name ??
                      item?.raw?.categoryName ??
                      ""
                    }
                    valueExpr="id"
                    value={model.documentCategoryId}
                    onValueChanged={(e) =>
                      handleInputChange(e, "documentCategoryId")
                    }
                    showClearButton
                    placeholder="Select category"
                  />
                  {errors.documentCategoryId && (
                    <div className="text-danger">
                      {errors.documentCategoryId}
                    </div>
                  )}
                </div>
              </div>

              {/* Document Name second */}
              <div className="col-12">
                <div className="form-group">
                  <label>
                    Document Name <span className="text-danger">*</span>
                  </label>
                  <TextBox
                    className="form-control form-control-sm"
                    height={37}
                    placeholder="Enter document name"
                    showClearButton
                    valueChangeEvent="keyup"
                    value={model.documentName}
                    onValueChanged={(e) => handleInputChange(e, "documentName")}
                  />
                  {errors.documentName && (
                    <div className="text-danger">{errors.documentName}</div>
                  )}
                </div>
              </div>

              <div className="col-6" style={{ paddingTop: "0.5rem" }}>
                <CheckBox
                  text="Issue Date Enabled"
                  value={!!model.isIssueDateEnable}
                  onValueChanged={(e) =>
                    handleInputChange(e.value, "isIssueDateEnable")
                  }
                />
              </div>

              <div className="col-6" style={{ paddingTop: "0.5rem" }}>
                <CheckBox
                  text="Expiry Date Enabled"
                  value={!!model.isExpiryDateEnable}
                  onValueChanged={(e) =>
                    handleInputChange(e.value, "isExpiryDateEnable")
                  }
                />
              </div>

              {/* swapped: Have Document No. moved here (was where Reminder was) */}
              <div className="col-6" style={{ paddingTop: "0.5rem" }}>
                <CheckBox
                  text="Have Document No."
                  value={!!model.haveDocumentNo}
                  onValueChanged={(e) =>
                    handleInputChange(e.value, "haveDocumentNo")
                  }
                />
              </div>

              {/* Is Renewable stays in same place */}
              <div className="col-6" style={{ paddingTop: "0.5rem" }}>
                <CheckBox
                  text="Is Renewable"
                  value={!!model.isRenewable}
                  onValueChanged={(e) =>
                    handleInputChange(e.value, "isRenewable")
                  }
                />
              </div>

              {/* swapped: Reminder Alert Enabled moved to where Have Document No. was */}
              <div className="col-6" style={{ paddingTop: "0.5rem" }}>
                <CheckBox
                  text="Reminder Alert Enabled"
                  value={!!model.isReminderAlertEnable}
                  onValueChanged={(e) =>
                    handleInputChange(e.value, "isReminderAlertEnable")
                  }
                />
              </div>

              {/* Show Reminder Days only when reminder is enabled - placed after the moved Reminder checkbox */}
              {model.isReminderAlertEnable && (
                <div className="col-6" style={{ paddingTop: "0.5rem" }}>
                  <div className="form-group">
                    <label>
                      Reminder Days{" "}
                      <span className="text-muted small">
                        (required when reminder enabled)
                      </span>
                    </label>
                    <NumberBox
                      value={model.reminderAlertDefaultDays}
                      onValueChanged={(e) =>
                        handleInputChange(e.value, "reminderAlertDefaultDays")
                      }
                      showSpinButtons={false}
                    />
                    {errors.reminderAlertDefaultDays && (
                      <div className="text-danger">
                        {errors.reminderAlertDefaultDays}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              {isSubmitted ? (
                <span>
                  <i className="fas fa-spinner fa-spin" /> In process
                </span>
              ) : (
                <span>
                  <i className="fas fa-check" /> Submit
                </span>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
